import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ChangeDetectorRef,
    inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogError } from '@memberjunction/core';

import {
    OutboundActionClient,
    GraphQLOutboundActionClient,
    BuilderStep,
    VoiceFinding,
    VoiceFindingSeverity,
} from '../../services/outbound-action.client';

/**
 * Builder workflow stages.
 *  - setup    : collect persona name / criteria / touch count
 *  - generating : the Generate action is running
 *  - refine   : editable cards + live voice check (the core surface)
 *  - creating : the Create action is running
 *  - created  : paused-confirmation modal is showing
 */
type BuilderStage = 'setup' | 'generating' | 'refine' | 'creating' | 'created';

/** A friendly chip the rail renders instead of a raw severity / ruleId. */
type FriendlyLabel = 'Fix' | 'Heads up' | 'Izzy';

/** One editable subject variant on a card. */
interface SubjectField {
    /** Variant letter A/B/C… (display only). */
    tag: string;
    value: string;
    /** Rendered HTML (with <mark>) for the highlight overlay behind the input. */
    highlightHtml: SafeHtml;
}

/**
 * One editable card. Card state is the SOURCE OF TRUTH — everything (lint,
 * create) is serialized from these. `body` is plain text for editing; the
 * incoming Reply HTML is converted on load and re-rendered to markdown on
 * serialize.
 */
interface BuilderCard {
    touchNumber: number;
    delayDays: number;
    delayLabel: string;
    subjects: SubjectField[];
    /** Plain-text body the user edits. */
    body: string;
    /** Backdrop HTML (with <mark>) shown behind the transparent textarea. */
    bodyHighlightHtml: SafeHtml;
    /** Per-card finding count (subjects + body). */
    findingCount: number;
}

/** A finding decorated for the rail (friendly label + which touch/where). */
interface RailFinding {
    finding: VoiceFinding;
    label: FriendlyLabel;
    /** CSS modifier class: 'err' | 'warn' | 'brand'. */
    chipClass: string;
}

const DEBOUNCE_MS = 350;
const DEFAULT_STEP_COUNT = 3;

/**
 * Sequence Builder — a guided "build a cold-email sequence" surface for a
 * non-technical marketer.
 *
 * Self-contained and ready to mount under the dashboard's Build tab (SA3).
 * Talks to the server ONLY through the Outbound actions via
 * {@link OutboundActionClient}; it has NO dependency on the Marketing package
 * and never re-implements voice rules — findings come from the lint action.
 *
 * Mount:
 *   <obd-sequence-builder [companyId]="selectedCompanyId" [brandName]="brand">
 *   </obd-sequence-builder>
 */
@Component({
    standalone: false,
    selector: 'obd-sequence-builder',
    templateUrl: './sequence-builder.component.html',
    styleUrls: ['../../styles/outbound-tokens.css', './sequence-builder.component.css'],
    providers: [
        GraphQLOutboundActionClient,
        { provide: OutboundActionClient, useExisting: GraphQLOutboundActionClient },
    ],
})
export class SequenceBuilderComponent implements OnChanges, OnDestroy {
    /** Company whose Reply workspace the sequence is created in. Required. */
    @Input() companyId = '';
    /** Optional brand label for the header / confirmation copy. */
    @Input() brandName = '';

    private readonly actionClient = inject(OutboundActionClient);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly cdr = inject(ChangeDetectorRef);

    // ─── Stage ───────────────────────────────────────────────────────────────
    stage: BuilderStage = 'setup';

    // ─── Setup form ──────────────────────────────────────────────────────────
    personaName = '';
    personaCriteria = '';
    stepCount = DEFAULT_STEP_COUNT;
    readonly stepOptions = [1, 2, 3, 4, 5, 6, 7, 8];

    // ─── Generation / create state ───────────────────────────────────────────
    isGenerating = false;
    isCreating = false;
    /** A blocking error from generate/create (lint errors NEVER land here). */
    errorMessage = '';
    /** True when the action records aren't synced to this environment. */
    notDeployed = false;

    // ─── Authoritative copy doc + cards ──────────────────────────────────────
    /** The doc returned by generate. We reserialize from cards after any edit. */
    private generatedDoc = '';
    /** The sequence name parsed out of the generated doc's frontmatter. */
    private sequenceName = '';
    cards: BuilderCard[] = [];

    // ─── Voice check ─────────────────────────────────────────────────────────
    railFindings: RailFinding[] = [];
    errorCount = 0;
    warningCount = 0;
    isLinting = false;
    /** Total findings across the sequence (errors + warnings). */
    get totalFindings(): number {
        return this.railFindings.length;
    }

    // ─── Confirmation modal ──────────────────────────────────────────────────
    createdDashboardUrl = '';
    createWarnings: string[] = [];

    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    /** Monotonic token so a stale lint response can't overwrite a newer one. */
    private lintToken = 0;

    // ─── Lifecycle ───────────────────────────────────────────────────────────

    ngOnChanges(changes: SimpleChanges): void {
        // If the host swaps the company while a draft is open, reset to setup so
        // we never create a sequence in the wrong brand's workspace.
        if (changes['companyId'] && !changes['companyId'].firstChange) {
            this.resetAll();
        }
    }

    ngOnDestroy(): void {
        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
        }
    }

    // ─── Header helpers ──────────────────────────────────────────────────────

    get headerTitle(): string {
        const audience = this.personaName.trim() || 'your audience';
        const brand = this.brandName.trim();
        return brand ? `Reach ${audience} for ${brand}` : `Reach ${audience}`;
    }

    get setupValid(): boolean {
        return (
            !!this.companyId &&
            this.personaName.trim().length > 0 &&
            this.personaCriteria.trim().length > 0 &&
            this.stepCount >= 1 &&
            this.stepCount <= 8
        );
    }

    // ─── Generate ────────────────────────────────────────────────────────────

    async onGenerate(): Promise<void> {
        if (!this.companyId) {
            this.errorMessage = 'Select a brand first.';
            return;
        }
        if (!this.setupValid) {
            this.errorMessage = 'Fill in the audience name and who they are before generating.';
            return;
        }
        this.errorMessage = '';
        this.notDeployed = false;
        this.isGenerating = true;
        this.stage = 'generating';
        try {
            const outcome = await this.actionClient.generateOutboundCopy(
                this.companyId,
                this.personaName.trim(),
                this.personaCriteria.trim(),
                this.stepCount,
            );
            if (outcome.kind === 'not-deployed') {
                this.notDeployed = true;
                this.errorMessage = outcome.message;
                this.stage = 'setup';
                return;
            }
            if (outcome.kind === 'error') {
                this.errorMessage = outcome.message;
                this.stage = 'setup';
                return;
            }
            this.generatedDoc = outcome.data.copyDocument;
            this.sequenceName = this.parseSequenceName(outcome.data.copyDocument);
            this.cards = outcome.data.steps.map((s, i) => this.toCard(s, i));
            this.stage = 'refine';
            // First lint: the generated doc is authoritative and not yet edited,
            // so lint it directly (saves a re-serialize round-trip). Findings
            // returned here are the same the rail/highlights consume.
            await this.lintNow(this.generatedDoc);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[SequenceBuilder] generate failed: ${msg}`);
            this.errorMessage = msg;
            this.stage = 'setup';
        } finally {
            this.isGenerating = false;
            this.cdr.detectChanges();
        }
    }

    /** "Regenerate all" — same as generate, keeps the setup inputs. */
    async onRegenerateAll(): Promise<void> {
        await this.onGenerate();
    }

    /** Return to the setup form to change the audience / touch count. */
    onChangeSetup(): void {
        this.stage = 'setup';
    }

    // ─── Editing → live voice check ──────────────────────────────────────────

    /** Called on every body/subject keystroke. Re-highlights now, lints debounced. */
    onEdit(): void {
        // Re-render highlights immediately from the LAST known findings so typing
        // feels responsive; the authoritative re-lint follows after the debounce.
        this.applyHighlights();
        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
            void this.lintNow(this.serialize());
        }, DEBOUNCE_MS);
    }

    /** Keep the body backdrop scroll-synced with its textarea. */
    onBodyScroll(card: BuilderCard, textarea: HTMLTextAreaElement, backdrop: HTMLElement): void {
        void card;
        backdrop.scrollTop = textarea.scrollTop;
        backdrop.scrollLeft = textarea.scrollLeft;
    }

    private async lintNow(doc: string): Promise<void> {
        if (!this.companyId || doc.trim().length === 0) return;
        const token = ++this.lintToken;
        this.isLinting = true;
        try {
            const outcome = await this.actionClient.lintSequenceCopy(this.companyId, doc);
            if (token !== this.lintToken) return; // a newer edit superseded this run
            if (outcome.kind !== 'success') {
                // Lint failures are non-blocking by design — surface nothing to the
                // user (Create stays enabled), but log so a broken lint is visible.
                LogError(`[SequenceBuilder] lint ${outcome.kind}: ${outcome.message}`);
                this.railFindings = [];
                this.errorCount = 0;
                this.warningCount = 0;
                this.applyHighlights();
                return;
            }
            this.errorCount = outcome.data.errorCount;
            this.warningCount = outcome.data.warningCount;
            this.railFindings = outcome.data.findings.map(f => this.toRailFinding(f));
            this.applyHighlights();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[SequenceBuilder] lint failed: ${msg}`);
            this.railFindings = [];
            this.errorCount = 0;
            this.warningCount = 0;
            this.applyHighlights();
        } finally {
            if (token === this.lintToken) {
                this.isLinting = false;
                this.cdr.detectChanges();
            }
        }
    }

    /** Re-render every body/subject backdrop + per-card counts from railFindings. */
    private applyHighlights(): void {
        for (const card of this.cards) {
            const bodyHits = this.railFindings.filter(
                rf => rf.finding.touchNumber === card.touchNumber && rf.finding.where === 'body',
            );
            card.bodyHighlightHtml = this.highlight(card.body, bodyHits.map(rf => rf.finding));

            const subjectHits = this.railFindings.filter(
                rf => rf.finding.touchNumber === card.touchNumber && rf.finding.where === 'subject',
            );
            for (const subject of card.subjects) {
                subject.highlightHtml = this.highlight(subject.value, subjectHits.map(rf => rf.finding));
            }
            card.findingCount = bodyHits.length + subjectHits.length;
        }
    }

    // ─── Push (create paused) ────────────────────────────────────────────────

    async onCreate(): Promise<void> {
        if (this.cards.length === 0) {
            this.errorMessage = 'Generate a draft before creating it in Reply.';
            return;
        }
        this.errorMessage = '';
        this.isCreating = true;
        this.stage = 'creating';
        try {
            const doc = this.serialize();
            const outcome = await this.actionClient.createReplySequence(
                this.companyId,
                doc,
                this.sequenceName || undefined,
            );
            if (outcome.kind === 'not-deployed') {
                this.notDeployed = true;
                this.errorMessage = outcome.message;
                this.stage = 'refine';
                return;
            }
            if (outcome.kind === 'error') {
                this.errorMessage = outcome.message;
                this.stage = 'refine';
                return;
            }
            this.createdDashboardUrl = outcome.data.dashboardUrl;
            this.createWarnings = outcome.data.warnings;
            this.stage = 'created';
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[SequenceBuilder] create failed: ${msg}`);
            this.errorMessage = msg;
            this.stage = 'refine';
        } finally {
            this.isCreating = false;
            this.cdr.detectChanges();
        }
    }

    /** Confirmation-modal copy — leads with "nothing has sent", adapts to findings. */
    get confirmationBody(): string {
        const n = this.cards.length;
        const brand = this.brandName.trim() || 'this brand';
        const open = this.totalFindings;
        if (open === 0) {
            return `Your ${n}-email sequence for ${this.audienceLabel} is in ${brand}'s Reply account, paused. No one is enrolled and no email goes out until someone reviews it and presses start in Reply.`;
        }
        return `Your ${n}-email sequence is in ${brand}'s Reply account, paused — even with ${open} optional note${open > 1 ? 's' : ''} still open. No one is enrolled and nothing sends until someone presses start in Reply.`;
    }

    private get audienceLabel(): string {
        return this.personaName.trim() || 'your audience';
    }

    onCloseModal(): void {
        this.stage = 'refine';
    }

    onDiscard(): void {
        this.resetAll();
    }

    // ─── Verdict helpers (template) ──────────────────────────────────────────

    get verdictIsOk(): boolean {
        return this.totalFindings === 0;
    }

    get verdictSubtitle(): string {
        if (this.verdictIsOk) return 'Nothing flagged. Ready to push.';
        return 'All optional — you can push anyway.';
    }

    get verdictTitle(): string {
        if (this.verdictIsOk) return 'On-brand';
        const n = this.totalFindings;
        return `${n} thing${n > 1 ? 's' : ''} to look at`;
    }

    // ─── Serialization (card state → simple-format markdown) ──────────────────

    /**
     * Serialize the card state into the parser's simple format:
     *
     *   ---
     *   sequence_name: <name>
     *   ---
     *
     *   ## Step 1
     *   **Delay:** 0 days
     *   **Subject options:**
     *   - Subject A
     *   - Subject B
     *
     *   Body paragraph one.
     *
     *   Body paragraph two.
     *
     * The parser keys off `## Step N`, `**Delay:** N days`, `**Subject options:**`
     * + bullet lines, then treats the remaining lines as the body. Bodies are
     * plain text (the parser wraps them in <p> via renderBodyHtml).
     */
    serialize(): string {
        const name = this.sequenceName || this.defaultSequenceName();
        const parts: string[] = [`---`, `sequence_name: ${name}`, `---`, ``];
        this.cards.forEach((card, i) => {
            parts.push(`## Step ${i + 1}`);
            parts.push(`**Delay:** ${card.delayDays} days`);
            const subjects = card.subjects.map(s => s.value.trim()).filter(s => s.length > 0);
            if (subjects.length > 0) {
                parts.push(`**Subject options:**`);
                for (const subject of subjects) {
                    parts.push(`- ${subject}`);
                }
            }
            parts.push(``);
            // Body: normalize CRLF, trim trailing whitespace; keep blank-line
            // paragraph breaks so renderBodyHtml splits paragraphs correctly.
            const body = card.body.replace(/\r\n/g, '\n').replace(/[ \t]+$/gm, '').trim();
            if (body.length > 0) {
                parts.push(body);
                parts.push(``);
            }
        });
        return parts.join('\n');
    }

    private defaultSequenceName(): string {
        const brand = this.brandName.trim();
        const audience = this.personaName.trim() || 'Outbound';
        return brand ? `${brand} — ${audience}` : audience;
    }

    /** Pull `sequence_name:` out of the generated doc's frontmatter. */
    private parseSequenceName(doc: string): string {
        const m = /^\s*sequence_name\s*:\s*(.+)$/m.exec(doc);
        return m ? m[1].trim() : this.defaultSequenceName();
    }

    // ─── BuilderStep → card (HTML body → plain text) ──────────────────────────

    private toCard(step: BuilderStep, index: number): BuilderCard {
        const subjects: SubjectField[] = step.subjects.map((value, j) => ({
            tag: String.fromCharCode(65 + j),
            value,
            highlightHtml: this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(value)),
        }));
        const body = this.htmlToText(step.body);
        return {
            touchNumber: index + 1,
            delayDays: step.delayDays,
            delayLabel: step.delayLabel,
            subjects,
            body,
            bodyHighlightHtml: this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(body)),
            findingCount: 0,
        };
    }

    /**
     * Convert Reply-ready HTML body into plain text for editing:
     *  - </p><p> and <br> → blank line / newline
     *  - strip remaining tags
     *  - decode the entities renderBodyHtml emits (&amp; &lt; &gt; &quot;)
     */
    private htmlToText(html: string): string {
        if (!html) return '';
        let text = html
            .replace(/\r\n/g, '\n')
            .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
            .replace(/<p[^>]*>/gi, '')
            .replace(/<\/p>/gi, '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<[^>]+>/g, '');
        text = this.decodeEntities(text);
        // Collapse 3+ newlines to a paragraph break; trim outer whitespace.
        return text.replace(/\n{3,}/g, '\n\n').trim();
    }

    private decodeEntities(text: string): string {
        return text
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');
    }

    // ─── Highlighting (inline <mark> from findings) ───────────────────────────

    /**
     * Render `text` with each finding's `match` wrapped in a <mark>. Non-
     * overlapping, left-to-right; first match wins on overlap. Output is
     * HTML-escaped (only our own <mark> tags are literal), and sanitized via
     * DomSanitizer for the backdrop overlay.
     */
    private highlight(text: string, findings: VoiceFinding[]): SafeHtml {
        if (findings.length === 0 || text.length === 0) {
            // Trailing newline keeps the backdrop's last line height in sync with
            // the textarea (matches the mockup technique).
            return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(text) + '\n');
        }
        interface Hit { start: number; end: number; sev: VoiceFindingSeverity; brand: boolean; }
        const hits: Hit[] = [];
        for (const f of findings) {
            if (!f.match) continue;
            let from = 0;
            let idx = text.indexOf(f.match, from);
            while (idx !== -1) {
                hits.push({ start: idx, end: idx + f.match.length, sev: f.severity, brand: f.isBrandRule === true });
                from = idx + f.match.length;
                idx = text.indexOf(f.match, from);
            }
        }
        hits.sort((a, b) => a.start - b.start);
        const chosen: Hit[] = [];
        let lastEnd = -1;
        for (const h of hits) {
            if (h.start >= lastEnd) {
                chosen.push(h);
                lastEnd = h.end;
            }
        }
        let html = '';
        let cursor = 0;
        for (const h of chosen) {
            html += this.escapeHtml(text.slice(cursor, h.start));
            const cls = this.markClass(h.sev, h.brand);
            html += `<mark class="${cls}">${this.escapeHtml(text.slice(h.start, h.end))}</mark>`;
            cursor = h.end;
        }
        html += this.escapeHtml(text.slice(cursor)) + '\n';
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    private markClass(sev: VoiceFindingSeverity, brand: boolean): string {
        if (brand) return 'ob-mark--brand';
        return sev === 'error' ? 'ob-mark--err' : 'ob-mark--warn';
    }

    // ─── Friendly label mapping ──────────────────────────────────────────────

    /**
     * Brand (per-company) findings render as the brand's own chip. The server
     * flags these with `isBrandRule` (set when the finding comes from a seeded
     * per-company Voice Lint Rule rather than a universal rule), so the UI never
     * has to guess from the ruleId.
     */
    private toRailFinding(finding: VoiceFinding): RailFinding {
        if (finding.isBrandRule === true) {
            return { finding, label: 'Izzy', chipClass: 'brand' };
        }
        if (finding.severity === 'error') {
            return { finding, label: 'Fix', chipClass: 'err' };
        }
        return { finding, label: 'Heads up', chipClass: 'warn' };
    }

    /** Brand chip label — uses the brand name when known, else "Brand". */
    get brandChipLabel(): string {
        return this.brandName.trim() || 'Brand';
    }

    /** "Email N · subject|body" descriptor for a rail row. */
    railWhere(finding: VoiceFinding): string {
        return `Email ${finding.touchNumber} · ${finding.where}`;
    }

    railLabelText(rf: RailFinding): string {
        // Brand chips show the brand name in place of the literal "Izzy" default.
        if (rf.chipClass === 'brand') return this.brandChipLabel;
        return rf.label;
    }

    // ─── Shared ──────────────────────────────────────────────────────────────

    private escapeHtml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    private resetAll(): void {
        if (this.debounceTimer !== null) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        this.stage = 'setup';
        this.cards = [];
        this.generatedDoc = '';
        this.sequenceName = '';
        this.railFindings = [];
        this.errorCount = 0;
        this.warningCount = 0;
        this.errorMessage = '';
        this.notDeployed = false;
        this.createdDashboardUrl = '';
        this.createWarnings = [];
        this.isGenerating = false;
        this.isCreating = false;
        this.isLinting = false;
    }

    /** trackBy for the card list. */
    trackCard(_index: number, card: BuilderCard): number {
        return card.touchNumber;
    }

    trackRail(index: number, _rf: RailFinding): number {
        return index;
    }
}

/** Stub to prevent tree-shaking from dropping the component registration. */
export function LoadSequenceBuilder(): void {
    // intentionally empty
}
